import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getDatabase,
  ref,
  onValue,
  off, set
} from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { AppContext } from './AppContext';




function numFromAny(n) {
  if (n == null) return 0;
  if (typeof n === 'number') return n;
  if (typeof n === 'string') return Number(n) || 0;
  if (typeof n === 'object') return numFromAny(Object.values(n)[0]);
  return 0;
}


const Tokens = () => {
  const { owned, blocks, currentUser } = useContext(AppContext);
  const navigate = useNavigate();

  /* Tabs */
  const [activeTab, setActiveTab] = useState('market'); // 'market' | 'holders'

  /* Modal state */
  const [mode, setMode]     = useState(null); // 'buy' | 'sell'
  const [qtyInput, setQty]  = useState('');
  const [priceInput, setPrice] = useState('');
  const [error, setError]   = useState('');

  /* Holders state (populated when the "Holders" tab is active) */
  const [walletsRaw, setWalletsRaw] = useState({}); // datchainwallets snapshot
  const [usersMap, setUsersMap]     = useState({}); // { uid: { walletId } }
  const [holdersLoading, setHoldersLoading] = useState(false);


async function replaceTokenBlocks() {
  const db = getDatabase();
  const blocksRef = ref(db, 'tokenBlocks');

  // Target total tokens across all blocks
  const TARGET_TOTAL = 100_000_000;

  // How many tokens each full block should hold (tune as you like)
  const BLOCK_SIZE = 1_000_000;

  // Compute number of full blocks and any remainder
  const fullBlocks = Math.floor(TARGET_TOTAL / BLOCK_SIZE);
  const remainder  = TARGET_TOTAL - (fullBlocks * BLOCK_SIZE);

  const newBlocks = {};
  let index = 1;

  // Create full blocks
  for (; index <= fullBlocks; index++) {
    newBlocks[index] = {
      price: '1.00',
      platformRemaining: BLOCK_SIZE,
      walletId: 'TDC'
    };
  }

  // Add a final block for any remainder to hit exactly 100,000,000
  if (remainder > 0) {
    newBlocks[index] = {
      price: '1.00',
      platformRemaining: remainder,
      walletId: 'TDC'
    };
  }

  try {
    await set(blocksRef, newBlocks);
    console.log('✅ tokenBlocks replaced successfully.');
  } catch (err) {
    console.error('💥 Failed to replace tokenBlocks', err);
  }
}



useEffect(() => {
  if (activeTab !== 'holders') return;

  const db = getDatabase();
  const rootRef = ref(db, 'datchainwallets');

  const unsub = onValue(
    rootRef,
    (snap) => {
      if (!snap.exists()) {
        setWalletsRaw({});
        return;
      }
      setWalletsRaw(snap.val() || {});
    },
    (err) => {
      console.error('💥 holders listener error', err);
      setWalletsRaw({});
    }
  );

  return () => off(rootRef, 'value', unsub);
}, [activeTab]);



const holders = useMemo(() => {
  const rows = Object.entries(walletsRaw).map(([uid, perBlock]) => {
    let total = 0;
    Object.values(perBlock || {}).forEach(v => {
      total += numFromAny(v?.qty);
    });

    const walletId = usersMap[uid]?.walletId || uid; // ← fallback to UID instead of 'UNKNOWN'
    return { uid, walletId, total };
  }).filter(r => r.total > 0);

  rows.sort((a, b) => b.total - a.total);
  return rows;
}, [walletsRaw, usersMap]);


const availableBlocks = useMemo(
  () => blocks.filter(b => Number(b.platformRemaining) > 0),
  [blocks]
);

const lowestBlock = availableBlocks.length
  ? availableBlocks.reduce((min, b) => (b.price < min.price ? b : min), availableBlocks[0])
  : null;

const bestAsk = availableBlocks.reduce((m, b) => Math.max(m, b.price), 0);
const dynamicCeil = Math.min(bestAsk * 1.5 || 0.35 * 1.5, 1.00);
const maxAllowed  = Math.min(dynamicCeil, bestAsk + 0.05);

/* Totals: only count platformRemaining for visible (available) blocks */
const availableAll = useMemo(
  () => availableBlocks.reduce((sum, b) => sum + Number(b.platformRemaining || 0), 0),
  [availableBlocks]
);


// DatChain token issuance + spot price (from RTDB metrics)
const DISTRIBUTABLE_MAX = 66_669;

const [poolCents, setPoolCents]       = useState(0);
const [circulating, setCirculating]   = useState(0);
const [issuedToUsers, setIssuedToUsers] = useState(0);

useEffect(() => {
  const db = getDatabase();
  const poolRef  = ref(db, 'metrics/datToken/poolCents');
  const circRef  = ref(db, 'metrics/datToken/circulating');
  const issueRef = ref(db, 'metrics/datToken/issuedToUsers');

  const unsubPool = onValue(poolRef, s => setPoolCents(Number(s.val() || 0)), () => setPoolCents(0));
  const unsubCirc = onValue(circRef, s => setCirculating(Number(s.val() || 0)), () => setCirculating(0));
  const unsubIss  = onValue(issueRef, s => setIssuedToUsers(Number(s.val() || 0)), () => setIssuedToUsers(0));

  return () => {
    off(poolRef, 'value', unsubPool);
    off(circRef, 'value', unsubCirc);
    off(issueRef, 'value', unsubIss);
  };
}, []);

const datTokenSpotUsd = useMemo(() => {
  const c = Number(circulating) || 0;
  const p = Number(poolCents) || 0;
  if (!c || c <= 0) return 0;
  return (p / 100) / c; // pool (USD) divided by circulating tokens
}, [poolCents, circulating]);

const issuableRemaining = useMemo(() => {
  const issued = Number(issuedToUsers) || 0;
  return Math.max(0, DISTRIBUTABLE_MAX - issued);
}, [issuedToUsers]);


  /* ---------- BUY FLOW (Stripe link) ---------- */
  const [isBuying, setIsBuying]     = useState(false);
  const [isListing, setIsListing]   = useState(false);

const openBuy = () => {
  if (!currentUser) { navigate('/login'); }
  if (!lowestBlock || isBuying || isListing) return;
  setMode('buy');
  setError('');
  // Prefill to the minimum required (or 1 if tiny remainder)
  // You may need to wait one tick to ensure buyLimits exists
  setTimeout(() => {
    setQty(String(buyLimits?.minTokens ?? 1));
  }, 0);
};


  // RESET any loading UI when the page becomes visible again (e.g., Back from Stripe)
useEffect(() => {
  const clearStripeState = () => {
    setIsBuying(false);
    setIsListing(false);
    setMode(null);
    if (sessionStorage.getItem('stripeRedirect')) {
      sessionStorage.removeItem('stripeRedirect');
    }
  };

  // Fires on bfcache restore and normal shows
  const onPageShow = () => clearStripeState();
  // Extra safety: when tab regains visibility
  const onVis = () => { if (!document.hidden) clearStripeState(); };

  // Also clear immediately on first mount if flag is present
  if (sessionStorage.getItem('stripeRedirect')) clearStripeState();

  window.addEventListener('pageshow', onPageShow);
  document.addEventListener('visibilitychange', onVis);
  return () => {
    window.removeEventListener('pageshow', onPageShow);
    document.removeEventListener('visibilitychange', onVis);
  };
}, []);

// Minimum USD purchase unless the block has < $25 left
const MIN_PURCHASE_USD = 25;

const buyLimits = useMemo(() => {
  if (!lowestBlock) return null;
  const price      = Number(lowestBlock.price || 0);
  const remaining  = Number(lowestBlock.platformRemaining || 0);
  const remainingValue = price * remaining;

  // If the block has less than $25 total left, allow buying any amount up to what's left
  const isTinyRemainder = remainingValue < MIN_PURCHASE_USD;

  // Otherwise, require at least $25 worth (rounded up in tokens)
  const minTokens = isTinyRemainder ? 1 : Math.ceil(MIN_PURCHASE_USD / price);
  const maxTokens = remaining;

  // For display only
  const minSpendDisplay = isTinyRemainder ? 0 : minTokens * price;

  return { price, remaining, remainingValue, isTinyRemainder, minTokens, maxTokens, minSpendDisplay };
}, [lowestBlock]);






const confirmBuy = async () => {
  if (!lowestBlock || isBuying) return;

  const qty = parseInt(qtyInput, 10);
  if (!qty || qty <= 0) { setError('Enter a positive number'); return; }

  // Enforce remaining cap first
  if (buyLimits && qty > buyLimits.maxTokens) {
    setError(`Only ${buyLimits.maxTokens.toLocaleString()} tokens remain in this block`);
    return;
  }

  // Enforce $25 minimum unless the block has < $25 total left
  if (buyLimits && !buyLimits.isTinyRemainder) {
    const spend = qty * buyLimits.price;
    if (spend < MIN_PURCHASE_USD) {
      setError(`Minimum purchase is $${MIN_PURCHASE_USD} (~${buyLimits.minTokens.toLocaleString()} tokens)`);
      return;
    }
  }

  try {
    setIsBuying(true);
    setError('');

    const functions  = getFunctions();
    const createLink = httpsCallable(functions, 'createPaymentLinkDatTokenTesting');
    const res        = await createLink({ blockId: lowestBlock.id, qty });

    const url = res?.data?.url;
    if (!url) throw new Error('Stripe link not returned');

    sessionStorage.setItem('stripeRedirect', '1');
    window.location.assign(url);
    setMode(null);
  } catch (err) {
    setError(err?.message || 'Purchase failed');
    setIsBuying(false);
  }
};

// up to 3 decimals, no forced trailing zeros
const fmt3 = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 });



  /* ---------- SELL FLOW ---------- */
  const openSell = () => {
    if (isBuying || isListing) return;
    setMode('sell'); setQty(''); setPrice(''); setError('');
  };

  const confirmSell = async () => {
    if (isListing) return;

    const qty   = parseInt(qtyInput, 10);
    const price = parseFloat(priceInput);

    if (!qty || qty <= 0)             { setError('Qty > 0'); return; }
    if (qty > owned)                  { setError('Not enough tokens'); return; }
    if (isNaN(price) || price < 0.01) { setError('Valid price'); return; }

    const priceId = String(Math.round(price * 100));
    const numId   = Number(priceId);
    if (numId < 36 || numId > 100) { setError('Choose $0.36 – $1.00'); return; }

    try {
      setIsListing(true);
      setError('');
      const fn = httpsCallable(getFunctions(), 'listDatChainTokens');
      await fn({ blockId: priceId, qty });
      setMode(null);
    } catch (err) {
      setError(err?.message || 'Listing failed');
    } finally {
      setIsListing(false);
    }
  };

  /* ---------- MODAL ---------- */
  const renderModal = () => {
    if (!mode) return null;
    const isBuy = mode === 'buy';
    return (
      <div className="modal-overlay" onClick={()=>{ if(!isBuying && !isListing) setMode(null); }}>
        <div className="modal-content" onClick={e=>e.stopPropagation()}>
          <h2>{isBuy ? 'Purchase Tokens' : 'Add Tokens to the Queue'}</h2>

{isBuy && lowestBlock && (
  <>
    <p className="modal-price">
      Price: <strong>${lowestBlock.price.toFixed(2)}</strong>
    </p>
    <p className="modal-remaining">
      {Number(lowestBlock.platformRemaining || 0).toLocaleString()} available
    </p>

    {buyLimits && (
      <p className="price-range">
        {buyLimits.isTinyRemainder
          ? 'No minimum – less than $25 remains in this block'
          : `Minimum: ~${buyLimits.minTokens.toLocaleString()} tokens ($${buyLimits.minSpendDisplay.toFixed(2)})`}
      </p>
    )}
  </>
)}


          {!isBuy && (
            <>
              <p className="modal-price">Your balance: <strong>{owned.toLocaleString()}</strong></p>
              <p className="price-range">
                Available Price Block Range:&nbsp;$0.36&nbsp;–&nbsp;${maxAllowed.toFixed(2)}
              </p>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={maxAllowed}
                value={priceInput}
                onChange={e=>setPrice(e.target.value)}
                placeholder="Price ($)"
                className="modal-input"
              />
            </>
          )}

<input
  type="number"
  min={buyLimits?.minTokens ?? 1}
  max={buyLimits?.maxTokens ?? undefined}
  step="10"
  value={qtyInput}
  onChange={e=>setQty(e.target.value)}
  placeholder="Quantity"
  className="modal-input"
/>


          {error && <p className="modal-error">{error}</p>}

          <div className="modal-actions">
            <button
              className="modal-btn cancel"
              onClick={()=>setMode(null)}
              disabled={isBuying || isListing}
            >
              Cancel
            </button>
            <button
              className="modal-btn confirm"
              onClick={isBuy ? confirmBuy : confirmSell}
              disabled={(isBuy && isBuying) || (!isBuy && isListing)}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* ---------- UI ---------- */
  return (
    <div className="homepage-container">




      {/* Top bar (only relevant for Market tab) */}
      {activeTab === 'market' && (
        <div className="totals-bar">
<div className="metric-card">
  <div className="metric-label">Market Place Utility Tokens</div>
  <div className="metric-value">{availableAll.toLocaleString()}</div>
</div>


<div className="metric-card">
  <div className="metric-label">DAT CHAIN Reward Tokens</div>
  <div className="metric-value">{issuableRemaining.toLocaleString()}</div>
</div>

<div className="metric-card">
  <div className="metric-label">DAT CHAIN Reward Token</div>
  <div className="metric-inline">
    <span className="num">${fmt3(datTokenSpotUsd)}</span>
    <span className="metric-label" style={{ marginLeft: '.35rem' }}>Current Value</span>
  </div>
</div>






        </div>
      )}

      {/* Market tab content */}
      {activeTab === 'market' && (
        <div className="assets-container">
          <ul className="assets-list">
  {availableBlocks.map(b => (

              <li
                key={b.id}
                className="asset-item"
                onClick={()=>navigate(`/tdc/block/${b.id}`)}
              >
                <img src="/THEDATCHAINlogo.png" alt="icon" className="token-icon-img"/>
                <div className="asset-info">
                  <div className="asset-creator">Link {b.id}</div>
                  <div className="asset-artist">${b.price.toFixed(2)} per token</div>
<div className="asset-song">
  {`Available: ${Number(b.platformRemaining || 0).toLocaleString()}`}
</div>

                </div>
              </li>
            ))}
          </ul>
        </div>
      )}



      {renderModal()}

      {(isBuying || isListing) && (
        <div className="blocking-overlay" role="dialog" aria-modal="true">
          <div className="blocking-content">
            <div className="spinner" />
            <div className="blocking-text">
              {isBuying ? 'Opening Stripe…' : 'Adding to Queue…'}
            </div>
          </div>
        </div>
      )}

      {/* STYLES */}
      <style>{`
  :root {
    --brand-top:   #fe24ad;
    --brand-bot:   #a410e8;
    --brand-grad:  linear-gradient(135deg, var(--brand-top), var(--brand-bot));
    --brand-top-20: rgba(254,36,173,.2);
    --brand-bot-40: rgba(164,16,232,.4);
  }

  *{margin:0;padding:0;box-sizing:border-box;}
  html,body{font-family:sans-serif;background:#000;color:#fff;}
  .homepage-container{min-height:100vh;padding:2rem;display:flex;flex-direction:column;}

  .streams-container{margin-bottom:1rem;font-weight:bold;}

  /* Tabs */
  .tabs{display:flex;gap:.5rem;margin-bottom:1rem;}
  .tab-btn{
    background:#0b0611;
    color:#ccc;
    border:1px solid rgba(255,255,255,.12);
    border-radius:8px;
    padding:.5rem .9rem;
    cursor:pointer;
  }
  .tab-btn.active{
    background:var(--brand-grad);
    color:#fff;
    border-color:transparent;
  }

  .totals-bar{
    display:flex;flex-wrap:wrap;gap:1rem;margin-bottom:1rem;align-items:center;
  }

  .purchase-btn,
  .sell-btn{
    padding:.6rem 1.4rem;
    border:none;
    border-radius:8px;
    font-weight:600;
    cursor:pointer;
    transition:filter .2s ease;
  }

  .purchase-btn{
    background:var(--brand-grad);
    color:#fff;
  }
  .purchase-btn:disabled{
    background:var(--brand-top-20);
    color:rgba(255,255,255,.5);
    cursor:not-allowed;
  }

  .sell-btn{
    background:var(--brand-grad);
    color:#fff;
  }
  .sell-btn:disabled{
    background:var(--brand-top-20);
    color:rgba(255,255,255,.5);
    cursor:not-allowed;
  }

  .assets-container{flex:1;overflow-y:auto;}
  .assets-list{
    display:grid;
    grid-template-columns:repeat(auto-fill,minmax(200px,1fr));
    gap:1rem;
    list-style:none;
  }

  .asset-item{
    display:flex;
    background:#050008;
    padding:1rem;
    border-radius:8px;
    border:1px solid var(--brand-bot-40);
    box-shadow:0 2px 6px rgba(0,0,0,.4);
    transition:transform .2s, box-shadow .2s;
    cursor:pointer;
  }
  .asset-item:hover{
    transform:translateY(-4px);
    box-shadow:0 4px 12px rgba(164,16,232,.3);
  }

  .token-icon-img{width:50px;height:50px;margin-right:1rem;object-fit:contain;}
.asset-info{ flex:1; min-width:0; }
/* replace your current .asset-info>div rule with this */
.asset-info>div{
  margin-bottom:.25rem;
  font-size:.9rem;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

/* replace your current .asset-song rule with this */
.asset-song{
  /* word-break:break-all;  ← remove this */
  font-size:.8rem;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

  .asset-artist{font-weight:bold;}
  .asset-song{word-break:break-all;font-size:.8rem;}

  /* Holders */
  .holders-container{flex:1;display:flex;flex-direction:column;gap:1rem;}
  .holders-loading, .holders-empty{
    display:flex;gap:.6rem;align-items:center;justify-content:center;opacity:.9;
  }
  .table-wrap{
    overflow:auto;
    border:1px solid rgba(255,255,255,.12);
    border-radius:10px;
    background:#050008;
    box-shadow:0 2px 6px rgba(0,0,0,.4);
  }
  .holders-table{
    width:100%;
    border-collapse:separate;
    border-spacing:0;
  }
  .holders-table th, .holders-table td{
    padding:.75rem .9rem;
    border-bottom:1px solid rgba(255,255,255,.06);
    text-align:left;
    font-size:.95rem;
  }
  .holders-table thead th{
    position:sticky;
    top:0;
    background:#0e0916;
    z-index:1;
  }
  .holders-table tbody tr:hover{
    background:rgba(164,16,232,.08);
  }
  .mono{font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace;}
  .uid{opacity:.7}

  /* MODAL */
  .modal-overlay{
    position:fixed;
    inset:0;
    background:rgba(0,0,0,.65);
    display:flex;
    align-items:center;
    justify-content:center;
    z-index:999;
  }
  .modal-content{
    background:#000;
    padding:2rem;
    border-radius:12px;
    max-width:400px;
    width:90%;
    border:1px solid var(--brand-bot-40);
    box-shadow:0 0 20px rgba(254,36,173,.15);
  }
  .modal-content h2{margin-top:0;color:#fff;}
  .modal-price strong,
  .modal-remaining,
  .price-range strong{
    background:var(--brand-grad);
    -webkit-background-clip:text;
    -webkit-text-fill-color:transparent;
  }

  .modal-input{
    width:100%;
    padding:.6rem .8rem;
    margin:.6rem 0;
    border:1px solid var(--brand-bot-40);
    border-radius:6px;
    background:#050008;
    color:#fff;
  }
  .modal-input:focus{
    outline:none;
    border-color:var(--brand-top);
    box-shadow:0 0 6px var(--brand-top);
  }

  .modal-error{color:var(--brand-top);font-size:.9rem;}
  .price-range{font-size:.85rem;color:#ccc;margin:.3rem 0;}

  .modal-actions{
    display:flex;
    justify-content:flex-end;
    gap:.75rem;
    margin-top:1rem;
  }
  .modal-btn{
    padding:.5rem 1.2rem;
    border:none;
    border-radius:6px;
    font-weight:600;
    cursor:pointer;
    transition:filter .2s ease;
  }
  .modal-btn.cancel{
    background:var(--brand-top-20);
    color:#fff;
  }
  .modal-btn.cancel:disabled{
    background:rgba(255,255,255,.1);
    cursor:not-allowed;
  }
  .modal-btn.confirm{
    background:var(--brand-grad);
    color:#fff;
  }
  .modal-btn.confirm:disabled{
    background:var(--brand-top-20);
    color:rgba(255,255,255,.5);
    cursor:not-allowed;
  }

  .totals-bar{
  display:grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap:1rem;
  margin-bottom:1.25rem;
  align-items:stretch;
}

.metric-card{
  background:rgba(255,255,255,.05);
  border:1px solid rgba(255,255,255,.10);
  border-radius:12px;
  padding:1rem;
  box-shadow:0 2px 8px rgba(0,0,0,.35);
}

.metric-label{
  font-size:.85rem;
  color:rgba(255,255,255,.75);
  margin-bottom:.25rem;
}

.metric-value{
  font-size:1.35rem;
  font-weight:900;
  background:var(--brand-grad);
  -webkit-background-clip:text;
  -webkit-text-fill-color:transparent;
}

.metric-sub{
  margin-top:.25rem;
  font-size:.8rem;
  color:rgba(255,255,255,.65);
}


  /* BLOCKING OVERLAY DURING ACTION */
  .blocking-overlay{
    position:fixed;
    inset:0;
    background:rgba(0,0,0,.8);
    display:flex;
    align-items:center;
    justify-content:center;
    z-index:1001;
  }
    /* single-line "123,456 available" display */
.metric-inline{
  display:flex;
  align-items:baseline;
}

/* keep the number in the same gradient "value" style */
.metric-inline .num{
  font-size:1.35rem;
  font-weight:900;
  background:var(--brand-grad);
  -webkit-background-clip:text;
  -webkit-text-fill-color:transparent;
}

/* make the word look like the header/label text */
.metric-inline .txt{
  margin-left:.35rem;
  font-size:.95rem;
  font-weight:700;
  color:rgba(255,255,255,.75);
  letter-spacing:.2px;
}

  .blocking-content{
    background:#050008;
    color:#fff;
    padding:1.25rem 1.5rem;
    border-radius:10px;
    min-width:220px;
    display:flex;
    flex-direction:column;
    align-items:center;
    gap:.75rem;
    box-shadow:0 10px 30px rgba(0,0,0,.45);
    border:1px solid var(--brand-bot-40);
  }
  .spinner{
    width:38px;
    height:38px;
    border:4px solid rgba(255,255,255,.15);
    border-top-color:var(--brand-top);
    border-radius:50%;
    animation:spin 1s linear infinite;
  }
  .blocking-text{font-weight:700;letter-spacing:.3px;}
  @keyframes spin{to{transform:rotate(360deg);}}
      `}</style>
    </div>
  );
};

export default Tokens;
